import { useTranslation } from 'react-i18next';
import { Group, Stack, Text } from '@mantine/core';
import { formatMoney } from '@/lib/format';
import type { BestPriceTone } from '@/types/bestPrice';

/** Сторона действия лучшей цены: продавец получает FCA, покупатель платит CPT. */
export type BestPriceAction = 'buy' | 'sell';

export type { BestPriceTone };

interface BestPriceLabelProps {
  /** Лучшая встречная цена, ₽/т (для sell — FCA у ворот, для buy — CPT с доставкой). */
  readonly price: number;
  /** Сторона: от неё зависит, «дороже» или «дешевле» означает выгоду. */
  readonly action: BestPriceAction;
  /**
   * Выгода относительно МОЕЙ цены, ₽/т (со знаком; >0 — выигрыш). Передаётся только там, где лучшая цена
   * привязана к моему предложению или прайс-листу; в поиске привязки нет, и выгода не выводится.
   */
  readonly advantage?: number;
  /** `md` — число 20 px в строках и карточках; `lg` — 28 px, главное число превью встречного предложения. */
  readonly size?: 'md' | 'lg';
}

// Лучшая цена на языке «Бюллетеня»: крупное число с «₽/т», под ним выгода к моей цене. Цвет числа обычный:
// выигрыш или потерю передаёт строка выгоды. Действие — отдельная кнопка (BestPriceButton, BestPriceLink).
export default function BestPriceLabel({ price, action, advantage, size = 'md' }: BestPriceLabelProps) {
  const { t } = useTranslation();

  // Испорченная цена (NaN из нераспознанной строки сервера) — лучше ничего, чем «NaN ₽/т».
  if (!Number.isFinite(price)) {
    return null;
  }

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Group gap={6} align="baseline" wrap="nowrap">
        <Text fz={size === 'lg' ? 28 : 20} fw={600} lh={1.15} style={{ whiteSpace: 'nowrap' }}>
          {formatMoney(price)}
        </Text>
        <Text size="sm" c="gray.7" style={{ whiteSpace: 'nowrap' }}>
          {t('app.bestPrice.rubPerTon')}
        </Text>
      </Group>
      {advantage != null ? <AdvantageHint advantage={advantage} action={action} /> : null}
    </Stack>
  );
}

// Выгода к моей цене: насколько рыночная цена ВЫШЕ или НИЖЕ моей. Слово несёт направление («Дороже»/«Дешевле»
// на N), цвет — выгоду: зелёный — выгодно, красный — невыгодно, серый — ровно моя цена. `advantage`
// нормализован так, что >0 = выигрыш для обеих сторон; но «дороже/дешевле» зависит и от стороны: продавцу
// выигрыш — рынок дороже, покупателю — рынок дешевле. Цвет — тон 7 (контраст AA на белом; тон 6 не проходит).
export function AdvantageHint({ advantage, action }: { readonly advantage: number; readonly action: BestPriceAction }) {
  const { t } = useTranslation();

  // Битое значение (NaN/Infinity из нераспознанной строки сервера) — лучше ничего.
  if (!Number.isFinite(advantage)) {
    return null;
  }

  // Разница в пределах рубля (округляется к 0) — «ровно моя цена», нейтрально.
  if (Math.round(advantage) === 0) {
    return (
      <Text size="sm" fw={600} c="gray.7">
        {t('app.bestPrice.exact')}
      </Text>
    );
  }

  const isGain = advantage > 0;
  // Рынок ДОРОЖЕ моей цены, когда: продажа+выигрыш (платят больше) ИЛИ покупка+проигрыш (просят больше).
  const marketHigher = (action === 'sell') === isGain;
  const color = isGain ? 'var(--mantine-color-gain-7)' : 'var(--mantine-color-loss-7)';
  const text = t(marketHigher ? 'app.bestPrice.higherBy' : 'app.bestPrice.lowerBy', {
    amount: formatMoney(Math.abs(advantage)),
  });

  return (
    <Text size="sm" fw={600} style={{ color }}>
      {text}
    </Text>
  );
}
